(() => {
  if (customElements.get('nordsign-db-menu')) return;
  let instance = 0;
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };
  class DatabaseMenu extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const data = this.closest('.shopify-section')?.querySelector('[data-ns-menu-data]') || document.querySelector('[data-ns-menu-data]');
      if (!data) return;
      try {
        this.model = window.nordsignCategoryModel(
          JSON.parse(data.querySelector('[data-category-tree]').textContent),
          JSON.parse(data.querySelector('[data-category-live]').textContent)
        );
        this.rootPath = (this.dataset.root || '/').replace(/\/$/, '');
        this.idPrefix = `ns-db-menu-${++instance}`;
        this.dataset.source = this.model.source;
        this.render();
        this.ready = true;
      } catch (error) {
        console.warn('Product category menu could not load', error);
      }
    }
    arrow() {
      const icon = this.querySelector('[data-menu-arrow]').content.firstElementChild.cloneNode(true);
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
      return icon;
    }
    previews(root) {
      const examples = node('div', 'ns-db-menu__previews');
      const rows = new Map(this.model.rows.map(row => [row.key, row]));
      for (const source of this.querySelectorAll(`[data-menu-preview-root="${root.key}"]`)) {
        const row = rows.get(source.dataset.menuPreviewCategory);
        if (!row?.url) continue;
        let ancestor = row;
        while (ancestor && ancestor.key !== root.key) ancestor = rows.get(ancestor.parent);
        if (!ancestor) continue;
        const link = this.link(row, 'ns-db-menu__preview');
        const media = node('span', 'ns-db-menu__preview-image');
        media.append(source.content.firstElementChild.cloneNode(true));
        link.replaceChildren(media, node('span', 'ns-db-menu__preview-name', row.name));
        examples.append(link);
      }
      return examples;
    }
    link(row, className = '') {
      const link = node('a', className, row.name);
      link.href = this.rootPath + row.url;
      if (location.pathname === this.rootPath + row.url) link.setAttribute('aria-current', 'page');
      return link;
    }
    list(parent) {
      const list = node('ul', 'ns-db-menu__links');
      for (const row of this.model.children(parent)) {
        const item = node('li');
        item.append(row.url ? this.link(row) : node('span', '', row.name));
        if (this.model.children(row.key).length) item.append(this.list(row.key));
        list.append(item);
      }
      return list;
    }
    panel(root) {
      const panel = node('div', 'ns-db-menu__panel');
      panel.dataset.category = root.key;
      const heading = root.url ? this.link(root, 'ns-db-menu__title') : node('h2', 'ns-db-menu__title', root.name);
      if (root.url) heading.append(this.arrow());
      panel.append(heading);
      const groups = node('div', 'ns-db-menu__groups');
      for (const row of this.model.children(root.key)) {
        const group = node('section', 'ns-db-menu__group');
        const title = node('h3');
        const label = row.url ? this.link(row) : node('span', '', row.name);
        title.append(label);
        group.append(title);
        if (this.model.children(row.key).length) group.append(this.list(row.key));
        groups.append(group);
      }
      const body = node('div', 'ns-db-menu__body');
      body.append(groups);
      const examples = this.previews(root);
      if (examples.children.length) {
        body.classList.add('ns-db-menu__body--with-previews');
        body.append(examples);
      }
      panel.append(body);
      return panel;
    }
    render() {
      const content = this.querySelector('[data-menu-content]');
      const roots = this.model.children('root');
      this.buttons = [];
      this.panels = [];
      const mobile = this.dataset.mode === 'mobile';
      const layout = node('div', mobile ? 'ns-db-menu__mobile' : 'ns-db-menu__layout');
      const tabs = node('div', 'ns-db-menu__roots');
      const sidebar = node('div', 'ns-db-menu__sidebar');
      if (!mobile) {
        tabs.setAttribute('role', 'tablist');
        tabs.setAttribute('aria-label', this.dataset.label);
        tabs.setAttribute('aria-orientation', 'vertical');
        sidebar.append(tabs);
        layout.append(sidebar);
      }
      const panels = node('div', 'ns-db-menu__panels');
      roots.forEach((root, index) => {
        const button = node('button', 'ns-db-menu__root');
        button.type = 'button';
        button.id = `${this.idPrefix}-root-${root.key}`;
        button.dataset.category = root.key;
        button.append(node('span', '', root.name), this.arrow());
        const panel = this.panel(root);
        panel.id = `${this.idPrefix}-panel-${root.key}`;
        button.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', button.id);
        panel.hidden = true;
        this.buttons.push(button);
        this.panels.push(panel);
        if (mobile) {
          button.setAttribute('aria-expanded', 'false');
          const item = node('div', 'ns-db-menu__mobile-item');
          item.append(button, panel);
          layout.append(item);
          button.addEventListener('click', () => {
            const open = button.getAttribute('aria-expanded') !== 'true';
            this.buttons.forEach((b, i) => {
              b.setAttribute('aria-expanded', String(open && i === index));
              this.panels[i].hidden = !(open && i === index);
            });
          });
        } else {
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-selected', 'false');
          button.tabIndex = -1;
          panel.setAttribute('role', 'tabpanel');
          tabs.append(button);
          panels.append(panel);
          button.addEventListener('click', () => this.activate(index));
          button.addEventListener('pointermove', event => {
            // Scrolling or resizing under a stationary pointer must not change the active category.
            if (event.pointerType === 'mouse' && (event.movementX || event.movementY) && matchMedia('(hover: hover)').matches) this.activate(index);
          });
          button.addEventListener('focus', () => this.activate(index));
          button.addEventListener('keydown', event => {
            let next = index;
            if (event.key === 'ArrowDown') next = (index + 1) % roots.length;
            else if (event.key === 'ArrowUp') next = (index + roots.length - 1) % roots.length;
            else if (event.key === 'Home') next = 0;
            else if (event.key === 'End') next = roots.length - 1;
            else if (event.key === 'ArrowRight') {
              event.preventDefault();
              panel.querySelector('a')?.focus();
              return;
            } else return;
            event.preventDefault();
            this.buttons[next].focus();
          });
        }
      });
      if (!mobile) {
        // Keep the directory link beside the panels, outside the category tablist.
        const allCategories = this.querySelector('.ns-db-menu__all');
        if (allCategories) sidebar.append(allCategories);
        layout.append(panels);
      }
      content.replaceChildren(layout);
      if (!mobile && roots.length) this.activate(0);
      if (mobile) {
        this.addEventListener('keyup', event => {
          if (event.key !== 'Escape') return;
          const index = this.buttons.findIndex(button => button.getAttribute('aria-expanded') === 'true');
          if (index < 0) return;
          event.stopPropagation();
          this.buttons[index].setAttribute('aria-expanded', 'false');
          this.panels[index].hidden = true;
          this.buttons[index].focus();
        });
      }
    }
    activate(index) {
      const moveFocus = this.panels.some((panel, i) => i !== index && panel.contains(document.activeElement));
      this.buttons.forEach((button, i) => {
        button.setAttribute('aria-selected', String(i === index));
        button.tabIndex = i === index ? 0 : -1;
        this.panels[i].hidden = i !== index;
      });
      // Keep Dawn's focus-out handler from closing the menu when a focused panel is hidden.
      if (moveFocus) this.buttons[index].focus({preventScroll: true});
    }
  }
  customElements.define('nordsign-db-menu', DatabaseMenu);
})();
